import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const StarRating = ({ rating, onRate, interactive = false, size = 20 }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : undefined}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors`}
          disabled={!interactive}
        >
          <Star
            size={size}
            className={`${
              (interactive ? hover || rating : rating) >= star
                ? 'fill-[#D4AF37] text-[#D4AF37]'
                : 'text-[#57534E]'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ProductReviews = ({ productId, productName }) => {
  const { API, token, user, t } = useApp();
  
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newReview, setNewReview] = useState({
    rating: 0,
    title: '',
    content: ''
  });

  useEffect(() => {
    loadReviews();
  }, [productId]);

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(response.data.reviews);
      setStats({
        average_rating: response.data.average_rating,
        review_count: response.data.review_count
      });
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Log in om een review te plaatsen');
      return;
    }
    
    if (newReview.rating === 0) {
      toast.error('Selecteer een beoordeling (1-5 sterren)');
      return;
    }
    
    if (!newReview.title.trim() || !newReview.content.trim()) {
      toast.error('Vul alle velden in');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        {
          product_id: productId,
          rating: newReview.rating,
          title: newReview.title,
          content: newReview.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Review geplaatst!');
      setShowForm(false);
      setNewReview({ rating: 0, title: '', content: '' });
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review plaatsen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (reviewId) => {
    if (!token) {
      toast.error('Log in om te stemmen');
      return;
    }
    
    try {
      await axios.post(
        `${API}/reviews/${reviewId}/helpful`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bedankt voor je stem!');
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stemmen mislukt');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="mt-12" data-testid="product-reviews">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-heading text-2xl text-white mb-2">{t('customer_reviews')}</h2>
          <div className="flex items-center gap-3">
            <StarRating rating={stats.average_rating} size={24} />
            <span className="text-[#D4AF37] font-bold text-xl">{stats.average_rating.toFixed(1)}</span>
            <span className="text-[#A8A29E]">({stats.review_count} {t('based_on_reviews')})</span>
          </div>
        </div>
        
        {token && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
            data-testid="write-review-btn"
          >
            {t('write_review')}
          </button>
        )}
        
        {!token && (
          <p className="text-[#A8A29E] text-sm">
            <a href="/login" className="text-[#D4AF37] hover:underline">{t('login')}</a> {t('login_to_review').replace('Login to write a review', '').replace('Log in om een review te schrijven', '')}
          </p>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <h3 className="font-heading text-lg text-white mb-4">Jouw review voor {productName}</h3>
          
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <div>
              <label className="text-[#A8A29E] text-sm block mb-2">Je beoordeling *</label>
              <StarRating 
                rating={newReview.rating} 
                onRate={(rating) => setNewReview({...newReview, rating})}
                interactive={true}
                size={32}
              />
            </div>
            
            <div>
              <label className="text-[#A8A29E] text-sm block mb-2">Titel *</label>
              <input
                type="text"
                value={newReview.title}
                onChange={(e) => setNewReview({...newReview, title: e.target.value})}
                placeholder="Samenvatting van je ervaring"
                className="input-luxury w-full"
                maxLength={100}
                required
                data-testid="review-title-input"
              />
            </div>
            
            <div>
              <label className="text-[#A8A29E] text-sm block mb-2">{t('write_review')} *</label>
              <textarea
                value={newReview.content}
                onChange={(e) => setNewReview({...newReview, content: e.target.value})}
                placeholder={t('review_placeholder')}
                className="input-luxury w-full min-h-[120px] resize-none"
                maxLength={1000}
                required
                data-testid="review-content-input"
              />
              <p className="text-[#57534E] text-xs mt-1">{newReview.content.length}/1000</p>
            </div>
            
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary flex-1"
                data-testid="submit-review-btn"
              >
                {submitting ? '...' : t('submit_review')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setNewReview({ rating: 0, title: '', content: '' });
                }}
                className="btn-secondary"
              >
                {t('cancel_order')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="spinner mx-auto"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-[#57534E] mb-4" />
          <p className="text-[#A8A29E]">{t('no_reviews')}</p>
          <p className="text-[#57534E] text-sm mt-2">{t('write_review')}!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="glass rounded-xl p-6" data-testid={`review-${review.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-medium">{review.user_name}</span>
                    {review.is_verified_purchase && (
                      <span className="flex items-center gap-1 text-green-500 text-xs">
                        <CheckCircle size={12} />
                        Geverifieerde aankoop
                      </span>
                    )}
                  </div>
                  <StarRating rating={review.rating} size={16} />
                </div>
                <span className="text-[#57534E] text-sm">{formatDate(review.created_at)}</span>
              </div>
              
              <h4 className="text-white font-medium mb-2">{review.title}</h4>
              <p className="text-[#A8A29E] text-sm leading-relaxed">{review.content}</p>
              
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#2C1B18]">
                <button
                  onClick={() => handleHelpful(review.id)}
                  className="flex items-center gap-2 text-[#57534E] hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid={`helpful-btn-${review.id}`}
                >
                  <ThumbsUp size={16} />
                  {t('helpful')} ({review.helpful_votes})
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
